import type { ModelViewerElement } from '@google/model-viewer';
import { captureViewerCameraState } from '@/utils/annotationUtils';
import { reducePrecision } from '@/services/Utility';
import {
  PRODUCT_PREVIEW_CAPTURE_BG,
  PRODUCT_PREVIEW_CAPTURE_SIZE,
} from '@/constants/productPreview';

/** Visual style for annotation hotspots — keep in sync with `AnnotationHotspot`. */
const HOTSPOT_CAPTURE_STYLE = {
  background: 'rgba(15, 23, 42, 0.9)',
  borderColor: '#ffffff',
  textColor: '#ffffff',
  fontSizePx: 11,
  fontWeight: 600,
  shadowColor: 'rgba(0, 0, 0, 0.35)',
  shadowBlurPx: 4,
  shadowOffsetYPx: 1,
} as const;

export type ModelViewerCaptureOptions = {
  /** Composite slotted hotspot buttons onto the WebGL screenshot. */
  includeAnnotations?: boolean;
  /**
   * Rasterize the open annotation detail card onto the screenshot.
   * Defaults to `includeAnnotations` when omitted.
   */
  includeOpenAnnotationCard?: boolean;
};

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/** Wait for DOM/layout updates (e.g. annotation hotspots) before screenshotting. */
export function waitForViewerPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load model-viewer screenshot'));
    image.src = dataUrl;
  });
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('PNG export failed'))),
      'image/png'
    );
  });
}

function isHotspotVisible(hotspot: HTMLElement): boolean {
  const style = window.getComputedStyle(hotspot);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  const opacity = Number.parseFloat(style.opacity);
  if (!Number.isNaN(opacity) && opacity < 0.05) return false;

  const rect = hotspot.getBoundingClientRect();
  return rect.width >= 1 && rect.height >= 1;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function drawHotspotOnCanvas(
  ctx: CanvasRenderingContext2D,
  hotspot: HTMLElement,
  viewerRect: DOMRect,
  scaleX: number,
  scaleY: number
): void {
  const rect = hotspot.getBoundingClientRect();
  const x = (rect.left - viewerRect.left) * scaleX;
  const y = (rect.top - viewerRect.top) * scaleY;
  const width = rect.width * scaleX;
  const height = rect.height * scaleY;
  const radius = height / 2;
  const scale = Math.max(scaleX, scaleY);

  ctx.save();
  ctx.shadowColor = HOTSPOT_CAPTURE_STYLE.shadowColor;
  ctx.shadowBlur = HOTSPOT_CAPTURE_STYLE.shadowBlurPx * scale;
  ctx.shadowOffsetY = HOTSPOT_CAPTURE_STYLE.shadowOffsetYPx * scale;
  ctx.fillStyle = HOTSPOT_CAPTURE_STYLE.background;
  drawRoundedRect(ctx, x, y, width, height, radius);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = HOTSPOT_CAPTURE_STYLE.borderColor;
  ctx.lineWidth = Math.max(1, scale);
  drawRoundedRect(ctx, x, y, width, height, radius);
  ctx.stroke();

  const label = (hotspot.textContent ?? '').trim();
  if (label) {
    ctx.fillStyle = HOTSPOT_CAPTURE_STYLE.textColor;
    ctx.font = `${HOTSPOT_CAPTURE_STYLE.fontWeight} ${HOTSPOT_CAPTURE_STYLE.fontSizePx * scaleY}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + width / 2, y + height / 2);
  }
  ctx.restore();
}

function compositeAnnotationHotspots(
  ctx: CanvasRenderingContext2D,
  mv: ModelViewerElement,
  canvasWidth: number,
  canvasHeight: number
): void {
  const viewerRect = mv.getBoundingClientRect();
  if (viewerRect.width <= 0 || viewerRect.height <= 0) return;

  const scaleX = canvasWidth / viewerRect.width;
  const scaleY = canvasHeight / viewerRect.height;
  const hotspots = mv.querySelectorAll<HTMLElement>('button[slot^="hotspot-"]');

  for (const hotspot of hotspots) {
    if (!isHotspotVisible(hotspot)) continue;
    drawHotspotOnCanvas(ctx, hotspot, viewerRect, scaleX, scaleY);
  }
}

type AnnotationInfoBoxSnapshot = {
  title: string;
  bodyText: string;
  animationBadge: string;
  linkLabel: string;
  linkHref: string;
  imageSrc: string;
  accent: string;
  boxRect: { left: number; top: number; width: number; height: number };
  viewerRect: { left: number; top: number; width: number; height: number };
};

function snapshotOpenAnnotationInfoBox(mv: ModelViewerElement): AnnotationInfoBoxSnapshot | null {
  const infoBox = findOpenAnnotationInfoBox(mv);
  if (!infoBox) return null;

  const viewerRect = mv.getBoundingClientRect();
  const boxRect = infoBox.getBoundingClientRect();
  if (viewerRect.width <= 0 || viewerRect.height <= 0) return null;
  if (boxRect.width < 1 || boxRect.height < 1) return null;

  const overlapsViewer =
    boxRect.right > viewerRect.left &&
    boxRect.left < viewerRect.right &&
    boxRect.bottom > viewerRect.top &&
    boxRect.top < viewerRect.bottom;
  if (!overlapsViewer) return null;

  const link = infoBox.querySelector('a') as HTMLAnchorElement | null;
  const image = infoBox.querySelector('img') as HTMLImageElement | null;

  return {
    title: infoBox.querySelector('h4')?.textContent?.trim() ?? '',
    bodyText: infoBox.querySelector('p')?.textContent?.trim() ?? '',
    animationBadge: infoBox.querySelector('[class*="animationBadge"]')?.textContent?.trim() ?? '',
    linkLabel: link?.textContent?.replace(/\s+/g, ' ').trim() ?? '',
    linkHref: link?.href ?? '',
    imageSrc: image?.currentSrc || image?.src || '',
    accent: getComputedStyle(infoBox).getPropertyValue('--annotation-accent').trim() || '#1c7ed6',
    boxRect: {
      left: boxRect.left,
      top: boxRect.top,
      width: boxRect.width,
      height: boxRect.height,
    },
    viewerRect: {
      left: viewerRect.left,
      top: viewerRect.top,
      width: viewerRect.width,
      height: viewerRect.height,
    },
  };
}

/**
 * Draw the open annotation info card onto the WebGL screenshot.
 * Uses canvas 2D (not html-to-image) so CSS `color-mix` / `backdrop-filter` cannot blank the card.
 */
async function compositeOpenAnnotationInfoBox(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  snapshot: AnnotationInfoBoxSnapshot
): Promise<void> {
  const { boxRect, viewerRect, title, bodyText, animationBadge, linkLabel, accent, imageSrc } =
    snapshot;
  const scaleX = canvasWidth / viewerRect.width;
  const scaleY = canvasHeight / viewerRect.height;
  const x = (boxRect.left - viewerRect.left) * scaleX;
  const y = (boxRect.top - viewerRect.top) * scaleY;
  const width = boxRect.width * scaleX;
  const height = boxRect.height * scaleY;

  const padX = Math.max(12, width * 0.05);
  const padY = Math.max(10, height * 0.04);
  const titleSize = Math.max(13, width * 0.055);
  const bodySize = Math.max(11, width * 0.045);
  const badgeSize = Math.max(9, width * 0.038);
  const radius = Math.min(14 * Math.max(scaleX, scaleY), width / 8);

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.38)';
  ctx.shadowBlur = 18 * Math.max(scaleX, scaleY);
  ctx.shadowOffsetY = 8 * Math.max(scaleX, scaleY);
  ctx.fillStyle = 'rgb(18, 20, 26)';
  drawRoundedRect(ctx, x, y, width, height, radius);
  ctx.fill();
  ctx.shadowColor = 'transparent';

  // Accent bar
  ctx.fillStyle = accent || '#1c7ed6';
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + 3 * Math.max(scaleY, 1));
  ctx.lineTo(x, y + 3 * Math.max(scaleY, 1));
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = Math.max(1, scaleX);
  drawRoundedRect(ctx, x, y, width, height, radius);
  ctx.stroke();

  let cursorY = y + padY + 6 * Math.max(scaleY, 1);
  const textMaxWidth = width - padX * 2;

  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  if (title) {
    ctx.fillStyle = '#f8fafc';
    ctx.font = `700 ${titleSize}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    for (const line of wrapCanvasText(ctx, title, textMaxWidth).slice(0, 3)) {
      ctx.fillText(line, x + padX, cursorY);
      cursorY += titleSize * 1.35;
    }
    cursorY += 4 * Math.max(scaleY, 1);
  }

  if (animationBadge) {
    const badgePadX = 8 * Math.max(scaleX, 1);
    const badgePadY = 3 * Math.max(scaleY, 1);
    ctx.font = `700 ${badgeSize}px system-ui, -apple-system, sans-serif`;
    const badgeText = animationBadge.toUpperCase();
    const badgeW = ctx.measureText(badgeText).width + badgePadX * 2;
    const badgeH = badgeSize + badgePadY * 2;
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    drawRoundedRect(ctx, x + padX, cursorY, badgeW, badgeH, badgeH / 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.14)';
    ctx.lineWidth = 1;
    drawRoundedRect(ctx, x + padX, cursorY, badgeW, badgeH, badgeH / 2);
    ctx.stroke();
    ctx.fillStyle = accent || '#1c7ed6';
    ctx.textBaseline = 'middle';
    ctx.fillText(badgeText, x + padX + badgePadX, cursorY + badgeH / 2);
    ctx.textBaseline = 'top';
    cursorY += badgeH + 8 * Math.max(scaleY, 1);
  }

  if (bodyText) {
    ctx.fillStyle = 'rgba(248,250,252,0.84)';
    ctx.font = `400 ${bodySize}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    const lines = wrapCanvasText(ctx, bodyText, textMaxWidth).slice(0, 10);
    for (const line of lines) {
      if (cursorY + bodySize > y + height - padY) break;
      ctx.fillText(line, x + padX, cursorY);
      cursorY += bodySize * 1.55;
    }
    cursorY += 6 * Math.max(scaleY, 1);
  }

  if (linkLabel) {
    ctx.font = `600 ${Math.max(10, bodySize * 0.95)}px system-ui, -apple-system, sans-serif`;
    const linkPadX = 10 * Math.max(scaleX, 1);
    const linkPadY = 6 * Math.max(scaleY, 1);
    const linkW = Math.min(textMaxWidth, ctx.measureText(linkLabel).width + linkPadX * 2);
    const linkH = Math.max(10, bodySize * 0.95) + linkPadY * 2;
    if (cursorY + linkH <= y + height - padY / 2) {
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      drawRoundedRect(ctx, x + padX, cursorY, linkW, linkH, 8 * Math.max(scaleX, 1));
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, x + padX, cursorY, linkW, linkH, 8 * Math.max(scaleX, 1));
      ctx.stroke();
      ctx.fillStyle = '#f8fafc';
      ctx.textBaseline = 'middle';
      ctx.fillText(linkLabel, x + padX + linkPadX, cursorY + linkH / 2, linkW - linkPadX * 2);
      ctx.textBaseline = 'top';
      cursorY += linkH + 8 * Math.max(scaleY, 1);
    }
  }

  if (imageSrc) {
    const thumbH = Math.min(120 * scaleY, Math.max(40, y + height - cursorY - padY));
    if (thumbH > 24) {
      try {
        const thumb = await loadImageFromUrl(imageSrc);
        const thumbW = textMaxWidth;
        const drawH = Math.min(thumbH, (thumb.naturalHeight / Math.max(1, thumb.naturalWidth)) * thumbW);
        const thumbRadius = 10 * Math.max(scaleX, 1);
        ctx.save();
        drawRoundedRect(ctx, x + padX, cursorY, thumbW, drawH, thumbRadius);
        ctx.clip();
        ctx.drawImage(thumb, x + padX, cursorY, thumbW, drawH);
        ctx.restore();
        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, x + padX, cursorY, thumbW, drawH, thumbRadius);
        ctx.stroke();
      } catch {
        // Thumbnail is optional — card text is enough.
      }
    }
  }

  ctx.restore();
}

function findOpenAnnotationInfoBox(mv: ModelViewerElement): HTMLElement | null {
  const stage = mv.parentElement;
  if (stage) {
    const local = stage.querySelector<HTMLElement>('[data-annotation-info-box]');
    if (local) return local;
  }
  return document.querySelector<HTMLElement>('[data-annotation-info-box]');
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load annotation image'));
    image.src = url;
  });
}

function wrapCanvasText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const paragraphs = text.split('\n');
  const lines: string[] = [];
  for (const paragraph of paragraphs) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      continue;
    }
    let current = words[0];
    for (let i = 1; i < words.length; i += 1) {
      const next = `${current} ${words[i]}`;
      if (ctx.measureText(next).width <= maxWidth) {
        current = next;
      } else {
        lines.push(current);
        current = words[i];
      }
    }
    lines.push(current);
  }
  return lines;
}

/** Wait until slotted hotspot buttons exist and model-viewer has laid them out. */
export async function waitForAnnotationHotspots(mv: ModelViewerElement): Promise<void> {
  for (let attempt = 0; attempt < 24; attempt += 1) {
    await waitForViewerPaint();
    const hotspots = mv.querySelectorAll<HTMLElement>('button[slot^="hotspot-"]');
    if (hotspots.length === 0) continue;
    if (Array.from(hotspots).some(isHotspotVisible)) return;
  }
}

/**
 * Screenshot from model-viewer without side effects.
 * `toBlob()` triggers an internal resize that nudges the live camera zoom each capture.
 * Hotspots are composited manually — `toDataURL()` only returns the WebGL canvas.
 */
export async function captureModelViewerPreviewBlob(
  mv: ModelViewerElement,
  options: ModelViewerCaptureOptions = {}
): Promise<Blob> {
  const { includeAnnotations = false } = options;
  const includeOpenAnnotationCard = options.includeOpenAnnotationCard ?? includeAnnotations;
  // Snapshot before any await — open card must survive async image loads.
  const openCardSnapshot =
    includeOpenAnnotationCard ? snapshotOpenAnnotationInfoBox(mv) : null;
  const dataUrl = mv.toDataURL('image/png');

  if (!includeAnnotations && !openCardSnapshot) {
    return dataUrlToBlob(dataUrl);
  }

  const baseImage = await loadImageFromDataUrl(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = baseImage.width;
  canvas.height = baseImage.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }

  ctx.drawImage(baseImage, 0, 0);
  if (includeAnnotations) {
    compositeAnnotationHotspots(ctx, mv, canvas.width, canvas.height);
  }
  if (openCardSnapshot) {
    await compositeOpenAnnotationInfoBox(ctx, canvas.width, canvas.height, openCardSnapshot);
  }

  return canvasToPngBlob(canvas);
}

export type ModelViewerPreviewCapture = {
  url: string;
  cameraOrbit: string;
  cameraTarget: string;
  cameraFieldOfView: string;
};

/**
 * Snapshot + square preview PNG + reduced-precision camera attrs for product save.
 * Shared by manual "Current model view" and auto-capture on create.
 */
export async function capturePreviewFromViewer(
  mv: ModelViewerElement
): Promise<ModelViewerPreviewCapture> {
  const cameraSnapshot = captureViewerCameraState(mv);
  const rawBlob = await captureModelViewerPreviewBlob(mv);
  const url = await normalizeProductPreviewToObjectUrl(rawBlob);
  return {
    url,
    cameraOrbit: reducePrecision(cameraSnapshot.cameraOrbit),
    cameraTarget: reducePrecision(cameraSnapshot.cameraTarget),
    cameraFieldOfView: reducePrecision(cameraSnapshot.cameraFieldOfView),
  };
}

/** Letterbox into a square PNG the same way marketplace cards display previews (`object-fit: contain`). */
export async function normalizeProductPreviewImage(source: Blob | string): Promise<Blob> {
  const blob =
    typeof source === 'string'
      ? await (await fetch(source, { credentials: 'include' })).blob()
      : source;

  const bitmap = await createImageBitmap(blob);
  try {
    const size = PRODUCT_PREVIEW_CAPTURE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }

    ctx.fillStyle = PRODUCT_PREVIEW_CAPTURE_BG;
    ctx.fillRect(0, 0, size, size);

    const scale = Math.min(size / bitmap.width, size / bitmap.height);
    const drawW = bitmap.width * scale;
    const drawH = bitmap.height * scale;
    ctx.drawImage(bitmap, (size - drawW) / 2, (size - drawH) / 2, drawW, drawH);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error('Preview PNG export failed'))),
        'image/png'
      );
    });
  } finally {
    bitmap.close();
  }
}

export async function normalizeProductPreviewToObjectUrl(source: Blob | string): Promise<string> {
  const blob = await normalizeProductPreviewImage(source);
  return URL.createObjectURL(blob);
}

function sanitizeScreenshotFileName(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^\w\-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return cleaned || 'screenshot';
}

/**
 * Capture the current model-viewer frame (optional annotation hotspots) and trigger a PNG download.
 * Uses `toDataURL` so the live camera is not nudged (unlike `toBlob()`).
 */
export async function downloadModelViewerScreenshot(
  mv: ModelViewerElement,
  options: ModelViewerCaptureOptions & { fileName?: string } = {}
): Promise<void> {
  const { fileName, ...captureOptions } = options;
  await waitForViewerPaint();
  const blob = await captureModelViewerPreviewBlob(mv, {
    includeAnnotations: true,
    ...captureOptions,
  });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    const base = sanitizeScreenshotFileName(fileName ?? 'robotop-screenshot');
    anchor.href = url;
    anchor.download = `${base}.png`;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
  // WebKit may abort the download if the blob URL is revoked synchronously after click().
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
